import { Suspense, useEffect, type ReactNode } from 'react';
import { lazyRetry } from '@/lib/lazy-retry';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type React from 'react';

import { territoryMayVisit } from '@/lib/territory';
import { Lock, RotateCcw } from 'lucide-react';
import { BILLING_DESK_ROLES, ORG_IDS, isCeoDesk, isGuardRole, type PermissionKey, type RoleKey } from '@bright/shared';
import { isCounterRole, isStoreDesk } from '@/lib/roles';
import { useAuth } from '@/lib/auth';
import { useActingUser } from '@/lib/cover';
import { useActiveOrg } from '@/lib/active-org';
import LoginPage from '@/features/auth/LoginPage';
import { AppShell } from './AppShell';
import { Button, EmptyState, Skeleton } from './ui';

/**
 * The route table.
 *
 * Every authenticated page is a lazy chunk: an ASM who only ever opens the dashboard
 * and a stock list should not download the administration screens or the charting
 * library. The login page is the one exception — it is the first screen anyone sees,
 * and lazy-loading it buys a second network roundtrip and a boot-screen flash on
 * every cold visit, in exchange for keeping ~4 KB out of a bundle the visitor is
 * about to download anyway. Every authenticated route sits behind two guards — one
 * that asks whether there is a session at all, and one that asks whether this user
 * holds the permission the page needs — so a missing permission is a civil page
 * rather than a burst of failed requests and an empty table.
 */

const DashboardPage = lazyRetry('DashboardPage', () => import('@/features/dashboard/DashboardPage'));
const OemSalesPage = lazyRetry('OemSalesPage', () => import('@/features/oem/OemSalesPage'));
const ReturnsPage = lazyRetry('ReturnsPage', () => import('@/features/oem/ReturnsPage'));
// A mitr hands a vehicle to another mitr; the approver answers here (13 Oct 2026).
const TransfersPage = lazyRetry('TransfersPage', () => import('@/features/transfers/TransfersPage'));
const AutozoneStorePage = lazyRetry('AutozoneStorePage', () => import('@/features/oem/AutozoneStorePage'));
const PdiPage = lazyRetry('PdiPage', () => import('@/features/oem/PdiPage'));
const PurchaseDataPage = lazyRetry('PurchaseDataPage', () => import('@/features/oem/PurchaseDataPage'));
const ActivityPage = lazyRetry('ActivityPage', () => import('@/features/activity/ActivityPage'));
const LedgerPage = lazyRetry('LedgerPage', () => import('@/features/ledger/LedgerPage'));
const ProductionPage = lazyRetry('ProductionPage', () => import('@/features/oem/ProductionPage'));
const BuyerHomePage = lazyRetry('BuyerHomePage', () => import('@/features/buyer/BuyerHomePage'));
const EnquiriesPage = lazyRetry('EnquiriesPage', () => import('@/features/enquiries/EnquiriesPage'));
const LeadsPage = lazyRetry('LeadsPage', () => import('@/features/leads/LeadsPage'));
const AnnouncementsPage = lazyRetry('AnnouncementsPage', () => import('@/features/announcements/AnnouncementsPage'));
const FieldDeskPage = lazyRetry('FieldDeskPage', () => import('@/features/field/FieldDeskPage'));
const MyVerificationPage = lazyRetry('MyVerificationPage', () => import('@/features/verification/MyVerificationPage'));
const TasksPage = lazyRetry('TasksPage', () => import('@/features/tasks/TasksPage'));
const ApprovalsPage = lazyRetry('ApprovalsPage', () => import('@/features/approvals/ApprovalsPage'));
const CoversPage = lazyRetry('CoversPage', () => import('@/features/covers/CoversPage'));
const ShowroomsPage = lazyRetry('ShowroomsPage', () => import('@/features/showrooms/ShowroomsPage'));
const MyStockPhotosPage = lazyRetry('MyStockPhotosPage', () => import('@/features/stock-photos/MyStockPhotosPage'));
const StockPhotoRegisterPage = lazyRetry('StockPhotoRegisterPage', () => import('@/features/stock-photos/StockPhotoRegisterPage'));
const MeetingsPage = lazyRetry('MeetingsPage', () => import('@/features/field/MeetingsPage'));
const EnrolEmployeePage = lazyRetry('EnrolEmployeePage', () => import('@/features/admin/EnrolEmployeePage'));
const FieldTeamPage = lazyRetry('FieldTeamPage', () => import('@/features/field/FieldTeamPage'));
const MyExpensesPage = lazyRetry('MyExpensesPage', () => import('@/features/field/MyExpensesPage'));
const ReimbursePage = lazyRetry('ReimbursePage', () => import('@/features/field/ReimbursePage'));
const AttendancePage = lazyRetry('AttendancePage', () => import('@/features/attendance/AttendancePage'));
const EnquiryDeskPage = lazyRetry('EnquiryDeskPage', () => import('@/features/enquiries/EnquiryDeskPage'));
const FinanceReceiptsPage = lazyRetry('FinanceReceiptsPage', () => import('@/features/finance-receipts/FinanceReceiptsPage'));
const PurchaseCheckPage = lazyRetry('PurchaseCheckPage', () => import('@/features/purchase-check/PurchaseCheckPage'));
const DealerOnboardingPage = lazyRetry('DealerOnboardingPage', () => import('@/features/dealer-onboarding/DealerOnboardingPage'));
const ExpenseReconPage = lazyRetry('ExpenseReconPage', () => import('@/features/expense-recon/ExpenseReconPage'));
const DayBookPage = lazyRetry('DayBookPage', () => import('@/features/day-book/DayBookPage'));
// The CEO's desk: the business on one page, and the chat that answers from it (26 Oct 2026).
const CeoPage = lazyRetry('CeoPage', () => import('@/features/ceo/CeoPage'));
const CeoAskPage = lazyRetry('CeoAskPage', () => import('@/features/ceo/CeoAskPage'));
const SwapsPage = lazyRetry('SwapsPage', () => import('@/features/swaps/SwapsPage'));
const PortalHomePage = lazyRetry('PortalHomePage', () => import('@/features/portal/PortalHomePage'));
const MySalesPage = lazyRetry('MySalesPage', () => import('@/features/portal/MySalesPage'));
const SoldPapersPage = lazyRetry('SoldPapersPage', () => import('@/features/portal/SoldPapersPage'));
const DeskPage = lazyRetry('DeskPage', () => import('@/features/desk/DeskPage'));
const ServiceDeskPage = lazyRetry('ServiceDeskPage', () => import('@/features/service-desk/ServiceDeskPage'));
const ProductionStorePage = lazyRetry('ProductionStorePage', () => import('@/features/factory/ProductionStorePage'));
const PaintShopPage = lazyRetry('PaintShopPage', () => import('@/features/factory/PaintShopPage'));
const HoStorePage = lazyRetry('HoStorePage', () => import('@/features/ho-store/HoStorePage'));
const FactoryRegisterPage = lazyRetry('FactoryRegisterPage', () => import('@/features/factory/FactoryRegisterPage'));
const ChatPage = lazyRetry('ChatPage', () => import('@/features/chat/ChatPage'));
const OrdersPage = lazyRetry('OrdersPage', () => import('@/features/desk/OrdersPage'));
const OrderDeskPage = lazyRetry('OrderDeskPage', () => import('@/features/orders/OrderDeskPage'));
const DispatchedPage = lazyRetry('DispatchedPage', () => import('@/features/desk/DispatchedPage'));
const DispatchFromAutozonePage = lazyRetry('DispatchFromAutozonePage', () => import('@/features/desk/DispatchFromAutozonePage'));
const BillingDeskPage = lazyRetry('BillingDeskPage', () => import('@/features/desk/BillingDeskPage'));
const FactoryStorePage = lazyRetry('FactoryStorePage', () => import('@/features/factory/FactoryStorePage'));
const MaterialPage = lazyRetry('MaterialPage', () => import('@/features/material/MaterialPage'));
const GateOutPage = lazyRetry('GateOutPage', () => import('@/features/gate/GateOutPage'));
const GateMovePage = lazyRetry('GateMovePage', () => import('@/features/gate/GateMovePage'));
const InboundPage = lazyRetry('InboundPage', () => import('@/features/factory/InboundPage'));
const ScanOutPage = lazyRetry('ScanOutPage', () => import('@/features/factory/ScanOutPage'));
const StickersPage = lazyRetry('StickersPage', () => import('@/features/factory/StickersPage'));
const BoxPage = lazyRetry('BoxPage', () => import('@/features/factory/BoxPage'));
const DriverHomePage = lazyRetry('DriverHomePage', () => import('@/features/deliveries/DriverHomePage'));
const AsmHomePage = lazyRetry('AsmHomePage', () => import('@/features/asm/AsmHomePage'));
const AsmPerformancePage = lazyRetry('AsmPerformancePage', () => import('@/features/asm/AsmPerformancePage'));
const AsmSalesPage = lazyRetry('AsmSalesPage', () => import('@/features/asm/AsmSalesPage'));
const BrightStockPage = lazyRetry('BrightStockPage', () => import('@/features/stock/BrightStockPage'));
const DealersPage = lazyRetry('DealersPage', () => import('@/features/network/DealersPage'));
const DealerDetailPage = lazyRetry('DealerDetailPage', () => import('@/features/network/DealerDetailPage'));
const SubDealersPage = lazyRetry('SubDealersPage', () => import('@/features/network/SubDealersPage'));
const SubDealerDetailPage = lazyRetry('SubDealerDetailPage', () => import('@/features/network/SubDealerDetailPage'));
const OutletsPage = lazyRetry('OutletsPage', () => import('@/features/network/OutletsPage'));
const OutletDetailPage = lazyRetry('OutletDetailPage', () => import('@/features/network/OutletDetailPage'));
const VehicleProfilePage = lazyRetry('VehicleProfilePage', () => import('@/features/vehicles/VehicleProfilePage'));
const PassportPage = lazyRetry('PassportPage', () => import('@/features/passport/PassportPage'));
const SearchPage = lazyRetry('SearchPage', () => import('@/features/search/SearchPage'));
const AgeingPage = lazyRetry('AgeingPage', () => import('@/features/ageing/AgeingPage'));
const AnalyticsPage = lazyRetry('AnalyticsPage', () => import('@/features/analytics/AnalyticsPage'));
const SalesPage = lazyRetry('SalesPage', () => import('@/features/analytics/SalesPage'));
const AlertsPage = lazyRetry('AlertsPage', () => import('@/features/alerts/AlertsPage'));
const FinanciersPage = lazyRetry('FinanciersPage', () => import('@/features/financiers/FinanciersPage'));
const ReportsPage = lazyRetry('ReportsPage', () => import('@/features/reports/ReportsPage'));
const SyncPage = lazyRetry('SyncPage', () => import('@/features/sync/SyncPage'));
const BusyPage = lazyRetry('BusyPage', () => import('@/features/busy/BusyPage'));
const QuickSalePage = lazyRetry('QuickSalePage', () => import('@/features/tvs/QuickSalePage'));
const TvsDispatchPage = lazyRetry('TvsDispatchPage', () => import('@/features/tvs/TvsDispatchPage'));
const TvsOrdersPage = lazyRetry('TvsOrdersPage', () => import('@/features/tvs/TvsOrdersPage'));
const DispatchPhotoPage = lazyRetry('DispatchPhotoPage', () => import('@/features/orders/DispatchPhotoPage'));
const UsersPage = lazyRetry('UsersPage', () => import('@/features/admin/UsersPage'));
const RolesPage = lazyRetry('RolesPage', () => import('@/features/admin/RolesPage'));
const SettingsPage = lazyRetry('SettingsPage', () => import('@/features/admin/SettingsPage'));
const PricesPage = lazyRetry('PricesPage', () => import('@/features/admin/PricesPage'));

/** Holds the layout steady while a route chunk arrives. */
function RouteFallback() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading">
      <Skeleton width="14rem" height="1.75rem" shape="block" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} height="6rem" shape="block" />
        ))}
      </div>
      <Skeleton height="20rem" shape="block" />
    </div>
  );
}

/** A full-page hold while the stored session is being checked at boot. */
function BootScreen() {
  const { unreachable, checking, retryNow, signInAgain } = useAuth();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper px-4">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <span
          aria-hidden
          className="flex h-9 w-9 items-center justify-center rounded-sm bg-accent font-data text-body font-bold text-accent-ink"
        >
          BW
        </span>
        {unreachable ? (
          // The server is away — an update, or the connection (9 Oct 2026). The
          // person stays signed in; until then this was the sign-in screen.
          <div role="status" className="flex flex-col items-center gap-3">
            <div>
              <p className="font-display text-body font-semibold text-ink">Can&rsquo;t reach the server</p>
              <p className="mt-1 text-caption leading-relaxed text-muted">
                The app may be updating, or the connection dropped. It keeps trying by itself, and you stay signed in.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" loading={checking} onClick={retryNow} leftIcon={<RotateCcw className="h-3.5 w-3.5" aria-hidden />}>
                Try now
              </Button>
              <Button variant="ghost" size="sm" onClick={signInAgain}>
                Sign in again
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-micro text-muted">Checking your session</p>
        )}
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <BootScreen />;
  if (!isAuthenticated) {
    // Remember where they were headed so signing in lands them there, not on the
    // dashboard — a bookmarked chassis number should survive a session timeout.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export function RequirePermission({
  permission,
  children,
}: {
  /** One key, or several of which any one opens the page. */
  permission: PermissionKey | readonly PermissionKey[];
  children: ReactNode;
}) {
  const { has, isLoading } = useAuth();

  if (isLoading) return <RouteFallback />;

  const keys: readonly PermissionKey[] = typeof permission === 'string' ? [permission] : permission;
  if (!keys.some((key) => has(key))) {
    return (
      <EmptyState
        icon={<Lock />}
        headline="You do not have access to this page"
        description="Your role does not include this permission. An administrator can grant it from Administration › Roles."
        action={{ label: 'Back to the dashboard', onClick: () => window.location.assign('/') }}
      />
    );
  }
  return <>{children}</>;
}

/** Composes both guards and the shell around a page. */
function Protected({
  permission,
  children,
}: {
  permission: PermissionKey | readonly PermissionKey[];
  children: ReactNode;
}) {
  return (
    <RequireAuth>
      <AppShell>
        <RequirePermission permission={permission}>
          <Suspense fallback={<RouteFallback />}>{children}</Suspense>
        </RequirePermission>
      </AppShell>
    </RequireAuth>
  );
}

/**
 * The home route depends on who is signed in. A buyer portal account has no
 * analytics permission and no whole-book dashboard to show, so it lands on its own
 * scoped home; everyone else gets the operations dashboard.
 */
/** The maker's desk (and a dispatch sign-in) works orders; anyone else lists the ones they took. */
function OrdersRoute() {
  const { user, has } = useAuth();
  const { wholesale } = useActiveOrg();
  /*
   * Who sees the DESK — the board of everyone's orders — rather than their own
   * thread.
   *
   * The maker's book, the dispatch account, and the distributor's own store
   * desk. That last one was missing (owner, 16 Sep 2026: "vivek dipatch isnt
   * opening"): a store desk holds `requests:read` and the server already hands
   * it the desk's view, but this gate asked for the wholesale book, and the
   * store desk sits in its distributor's book by definition. It was sent to the
   * requester's page instead — the orders it raises, not the orders it fills.
   */
  const desk = has('requests:read') && (wholesale || isStoreDesk(user?.role.key));
  if (desk || user?.role.key === 'DISPATCH') return <OrderDeskPage />;
  return <OrdersPage />;
}

/**
 * Autozone has dealers only (owner, 1 Oct 2026: "mitr and outlet only exsist
 * for bright on wheels and tvs or fab ev autozone only has dealers"), so the
 * mitr and outlet lists send its book to the dealers.
 */
function NotInMakerBook({ children }: { children: ReactNode }) {
  const { wholesale } = useActiveOrg();
  if (wholesale) return <Navigate to="/dealers" replace />;
  return <>{children}</>;
}

/** The dispatched register in the maker's book; elsewhere, the receiving side of it. */
function DispatchedRoute() {
  // The maker's register: the maker's book whole, a distributor's book for its
  // own orders (19 Sep 2026). Only the distributor's billing desk is sent to
  // the receiving side instead — its work is what arrives, not what was sent.
  const { user } = useAuth();
  if (!user?.organization?.wholesale && user?.role.key === 'BILLING') return <Navigate to="/from-autozone" replace />;
  return <DispatchedPage />;
}

function HomeRoute() {
  // In a book reached only by covering someone's work (20 Oct 2026), home is that work's.
  const user = useActingUser();
  const { wholesale, id: activeOrgId } = useActiveOrg();
  if (user?.role.key === 'FACTORY') {
    // The factory's store keeper lands on the item list (9 Sep 2026).
    return (
      <RequireAuth>
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <FactoryStorePage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    );
  }
  if (user?.role.key === 'DRIVER') {
    // A driver lands on their deliveries — the whole of their app (26 Sep 2026).
    return (
      <RequireAuth>
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <DriverHomePage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    );
  }
  if (user?.role.key === 'FACTORY_PDI') {
    // The factory's PDI hand lands on the queue he works (25 Sep 2026).
    return <Navigate to="/pdi" replace />;
  }
  if (user?.role.key === 'TELECALLER') {
    // The telecalling team lands on the owner's leads — their calls for today (13 Oct 2026).
    return <Navigate to="/leads" replace />;
  }
  if (user?.role.key === 'SERVICE') {
    // The service desk lands on the complaints it works (20 Oct 2026).
    return <Navigate to="/service-desk" replace />;
  }
  if (user?.role.key === 'PAINTER') {
    // The painter lands on the paint shop — the whole of their app (26 Oct 2026).
    return <Navigate to="/paint-shop" replace />;
  }
  if (isCeoDesk(user)) {
    // Sunny lands on his business, on one page (26 Oct 2026).
    return <Navigate to="/ceo" replace />;
  }
  if (isGuardRole(user?.role.key)) {
    // The guards land on their gate (24 Oct 2026): vehicles out — or, for the one switched on for it, the material.
    return <Navigate to={user?.movesMaterial ? '/material' : '/gate/out'} replace />;
  }
  if (user?.role.key === 'STAFF') {
    // Staff land on their day — attendance is the first thing done in it; tasks and expenses sit beside it (14 Oct 2026).
    return <Navigate to="/attendance" replace />;
  }
  if (user?.role.key === 'TVS_DISPATCH') {
    // The TVS dispatch hand lands on the dispatch (6 Oct 2026) — in the TVS
    // book. An account given the role elsewhere lands on search: the dispatch
    // page sends anyone outside TVS home, and home sent it back (9 Oct 2026).
    // Since 19 Oct 2026 his day starts at the orders coming in ("he will see tvs orders coming in").
    return <Navigate to={activeOrgId === ORG_IDS.tvs ? '/tvs-orders' : '/search'} replace />;
  }
  if (user && user.role.key !== 'BILLING' && BILLING_DESK_ROLES.includes(user.role.key as RoleKey)) {
    // The billing chain's desks (1 Oct 2026) land on the desk, on their own step.
    return <Navigate to="/billing" replace />;
  }
  if (user?.role.key === 'BILLING') {
    // The maker's billing desk lands on what has gone out — the page the bill
    // is written from (owner, 14 Sep 2026). A distributor's lands on its own
    // billing desk: the bills and policies against what the counters sold
    // (owner, 21 Sep 2026: "she will only see billing section in her portal").
    if (!user?.organization?.wholesale) return <Navigate to="/billing" replace />;
    return (
      <RequireAuth>
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <DispatchedPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    );
  }
  if (user?.role.key === 'DISPATCH') {
    // The dispatch desk lands on the order desk (9 Sep 2026).
    return (
      <RequireAuth>
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <OrderDeskPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    );
  }
  if (user?.role.key === 'ASM' && user.asmId) {
    /*
     * An area sales manager lands on their territory — mitras, stock, ageing,
     * performance, orders — never the whole-book dashboard (8 Sep 2026).
     *
     * Only one with a territory. `asmId` is what narrows every read, and an
     * account without one has been given the whole book on purpose (owner,
     * 14 Sep 2026, of Goswami: "complete book view only", then "should be able
     * to order"). Landing them on a territory home would show a page built
     * around a territory they do not have.
     */
    return (
      <RequireAuth>
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <AsmHomePage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    );
  }
  if (isCounterRole(user?.role.key)) {
    // An outlet, a mitr or a retail dealer gets the counter's home — stock and
    // the Sell button; a wholesale buyer (FAB EV's Autozone-side login) keeps
    // the fleet view.
    const scopeType = user?.scopeParty?.type;
    const counter =
      scopeType === 'COMPANY_OUTLET' ||
      scopeType === 'SUB_DEALER' ||
      // The store counter (8 Sep 2026): direct sales out of the store's own hands.
      scopeType === 'DISTRIBUTOR' ||
      (scopeType === 'DEALER' && user?.organization?.slug === 'bright-on-wheels');
    return (
      <RequireAuth>
        <AppShell>
          <Suspense fallback={<RouteFallback />}>{counter ? <PortalHomePage /> : <BuyerHomePage />}</Suspense>
        </AppShell>
      </RequireAuth>
    );
  }
  // OEM staff land on the sales board: an OEM has no stock, and its home
  // reading is what it dispatched, not what it holds.
  if (wholesale) {
    return (
      <Protected permission="analytics:read">
        <OemSalesPage />
      </Protected>
    );
  }
  return (
    <Protected permission="analytics:read">
      <DashboardPage />
    </Protected>
  );
}

function NotFound() {
  return (
    <EmptyState
      headline="That page does not exist"
      description="The link may be out of date, or the record may have been removed."
      action={{ label: 'Back to the dashboard', onClick: () => window.location.assign('/') }}
    />
  );
}

/**
 * Start every page at the top.
 *
 * React Router keeps the window's scroll position across a navigation, and
 * nothing in the app reset it. On a desktop it is barely noticed; on a phone a
 * counter scrolls a third of the way down their stock, taps a vehicle, and
 * lands a third of the way down the vehicle's page — past the chassis, past the
 * Sell button — with no clue that the screen even changed (owner, 16 Sep 2026:
 * "i see many glitches").
 *
 * A hash is left alone: that is a deliberate jump to a place on the page.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);
  return null;
}

/**
 * A territory ASM is kept to their stock and their orders by ADDRESS as well
 * as by rail (owner, 17 Sep 2026: "thats it"). Anything else — a bookmark, a
 * shared link — lands on home. Head ASMs carry no territory and pass through.
 */
function TerritoryBounds({ children }: { children: React.ReactNode }) {
  // As the person stands in the book on screen — a covered job there is not held to one's own area.
  const user = useActingUser();
  const { pathname } = useLocation();
  if (user?.role.key === 'ASM' && user.asmId && !territoryMayVisit(pathname)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <>
      <ScrollToTop />
    <TerritoryBounds>
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={<HomeRoute />} />

      <Route
        path="/stock"
        element={
          <Protected permission="vehicles:read">
            <BrightStockPage />
          </Protected>
        }
      />

      {/* The network desk branches inside: a buyer raises and reads its own
          thread, staff with requests:read see the organisation's inbox — so
          the gate is the session, not one permission. */}
      <Route
        path="/desk"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <DeskPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The service desk — complaints and walk-ins of Autozone, BOW and FAB EV, and the parts fitted (20 Oct 2026). */}
      <Route
        path="/service-desk"
        element={
          <Protected permission="service:desk">
            <Suspense fallback={<RouteFallback />}>
              <ServiceDeskPage />
            </Suspense>
          </Protected>
        }
      />

      {/* The paint shop (26 Oct 2026): a production-store chassis's colour, before its parts. */}
      <Route
        path="/paint-shop"
        element={
          <Protected permission="paint:write">
            <PaintShopPage />
          </Protected>
        }
      />

      {/* The production store's chassis, given their motor, controller and differential by the factory store (26 Oct 2026). */}
      <Route
        path="/production-store"
        element={
          <Protected permission="factory:read">
            <ProductionStorePage />
          </Protected>
        }
      />

      {/* The HEAD OFFICE store — batteries by serial, Arun's (23 Oct 2026). */}
      <Route
        path="/ho-store"
        element={
          <Protected permission="service:desk">
            <HoStorePage />
          </Protected>
        }
      />

      {/* Goods in by invoice, box stickers, and scanning out (21 Oct 2026). Before /factory/:id. */}
      <Route
        path="/factory/inbound/:id"
        element={
          <Protected permission="factory:read">
            <InboundPage />
          </Protected>
        }
      />
      <Route
        path="/factory/scan"
        element={
          <Protected permission="factory:read">
            <ScanOutPage />
          </Protected>
        }
      />
      {/* The stickers print alone — no menu, no header, one sticker a page. */}
      <Route
        path="/factory/stickers"
        element={
          <RequireAuth>
            <RequirePermission permission="factory:write">
              <Suspense fallback={<RouteFallback />}>
                <StickersPage />
              </Suspense>
            </RequirePermission>
          </RequireAuth>
        }
      />
      {/* Where a box sticker's QR lands on a phone. */}
      <Route
        path="/box/:code"
        element={
          <Protected permission="factory:read">
            <BoxPage />
          </Protected>
        }
      />

      {/* Every piece in and out of the factory store, day by day (20 Oct 2026). Before /factory/:id. */}
      <Route
        path="/factory/register"
        element={
          <Protected permission="factory:read">
            <Suspense fallback={<RouteFallback />}>
              <FactoryRegisterPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Company chat — every employee but the drivers; the page decides (20 Oct 2026). */}
      {['/chat', '/chat/:roomId'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <AppShell>
                <Suspense fallback={<RouteFallback />}>
                  <ChatPage />
                </Suspense>
              </AppShell>
            </RequireAuth>
          }
        />
      ))}

      {/* Material between the factory and the store — the guards' gate register (23 Oct 2026). */}
      <Route
        path="/material"
        element={
          <Protected permission="material:move">
            <Suspense fallback={<RouteFallback />}>
              <MaterialPage />
            </Suspense>
          </Protected>
        }
      />

      {/* The CEO's desk (26 Oct 2026): the server refuses anyone but the owner's desks. */}
      <Route
        path="/ceo"
        element={
          <Protected permission="vehicles:read">
            <Suspense fallback={<RouteFallback />}>
              <CeoPage />
            </Suspense>
          </Protected>
        }
      />
      <Route
        path="/ceo/ask"
        element={
          <Protected permission="vehicles:read">
            <Suspense fallback={<RouteFallback />}>
              <CeoAskPage />
            </Suspense>
          </Protected>
        }
      />

      {/* The gate: every dispatched vehicle, ticked as it leaves (23 Oct 2026). */}
      <Route
        path="/gate/in"
        element={
          <Protected permission="gate:out">
            <Suspense fallback={<RouteFallback />}>
              <GateMovePage direction="IN" />
            </Suspense>
          </Protected>
        }
      />
      <Route
        path="/gate/out"
        element={
          <Protected permission="gate:out">
            <Suspense fallback={<RouteFallback />}>
              <GateMovePage direction="OUT" />
            </Suspense>
          </Protected>
        }
      />
      <Route
        path="/gate"
        element={
          <Protected permission="gate:out">
            <Suspense fallback={<RouteFallback />}>
              <GateOutPage />
            </Suspense>
          </Protected>
        }
      />

      {/* The factory's own store — items and counts, not chassis (9 Sep 2026). */}
      <Route
        path="/factory"
        element={
          <Protected permission="factory:read">
            <Suspense fallback={<RouteFallback />}>
              <FactoryStorePage />
            </Suspense>
          </Protected>
        }
      />
      <Route
        path="/factory/:id"
        element={
          <Protected permission="factory:read">
            <Suspense fallback={<RouteFallback />}>
              <FactoryStorePage />
            </Suspense>
          </Protected>
        }
      />

      {/* The portal's desk, opened on one kind from the rail (8 Sep 2026). */}
      <Route
        path="/service"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <DeskPage kind="SERVICE" />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/my-orders"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <DeskPage kind="ORDER" />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* An ASM's performance — their mitras, month on month. */}
      <Route
        path="/performance"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <AsmPerformancePage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Everything that has gone out, with its form and photos (12 Sep 2026).
          The maker's page: a distributor's book is sent to its own side of it,
          "Dispatch from Autozone" (owner, 18 Sep 2026). */}
      <Route
        path="/dispatched"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <DispatchedRoute />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The owner's flyers and Yes/No questions to the counters (1 Oct 2026). */}
      <Route
        path="/announcements"
        element={
          <Protected permission="announcements:manage">
            <AnnouncementsPage />
          </Protected>
        }
      />

      {/* The ASMs' field desk — their own meetings, prospects and expenses (3 Oct 2026).
          The page sends anyone who is not an ASM home; the server refuses them too. */}
      <Route
        path="/field"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <FieldDeskPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Enrol an employee for an administrator's approval (7 Oct 2026). */}
      <Route
        path="/enrol-employee"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <EnrolEmployeePage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Tasks — the owner gives them, the employees do them; no permission, the page decides (12 Oct 2026). */}
      <Route
        path="/tasks"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <TasksPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Approvals — what waits for one's answer, from every book, and what one asked; the server decides what each person has (17 Oct 2026). */}
      <Route
        path="/approvals"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <ApprovalsPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      {/* The owner hands someone's work over while they are away (20 Oct 2026); the page sends anyone else home. */}
      <Route
        path="/covers"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <CoversPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Mitras' and outlets' showroom checks (23 Oct 2026): the office and the ASMs; the page sends anyone else home. */}
      {/* Weekly stock photos (26 Oct 2026): a counter's own round, and the register its ASM and the office read — each page sends anyone else home. */}
      <Route
        path="/stock-photos"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <MyStockPhotosPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/stock-photo-register"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <StockPhotoRegisterPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/showrooms"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <ShowroomsPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      {/* An ASM's meetings, one after another (23 Oct 2026); the page sends anyone else home. */}
      <Route
        path="/meetings"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <MeetingsPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* One's own expenses — every employee; no permission, the page decides (12 Oct 2026). */}
      <Route
        path="/expenses"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <MyExpensesPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Paying approved expenses back, with the payment's screenshot (19 Oct 2026). */}
      <Route
        path="/reimburse"
        element={
          <Protected permission="field:review">
            <ReimbursePage />
          </Protected>
        }
      />

      {/* Part swaps — the office's and the service desk's page; the page and the server decide who keeps it (22 Oct 2026). */}
      <Route
        path="/swaps"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <SwapsPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The cash day book — company cash paid out, by head, per book; the page decides who keeps it (21 Oct 2026). */}
      <Route
        path="/day-book"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <DayBookPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Expense reconciliation — the bank statement against the expense sheet, per book; the page decides who sees it (19 Oct 2026). */}
      <Route
        path="/expense-recon"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <ExpenseReconPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The owner's enquiries desk — everyone's enquiries across the books and their call backs; the page decides (14 Oct 2026). */}
      <Route
        path="/enquiry-desk"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <EnquiryDeskPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* FAB EV's purchase check (29 Oct 2026); the page and the server decide who keeps it. */}
      <Route
        path="/purchase-check"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <PurchaseCheckPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The financier's money against each loan (27 Oct 2026); the page and the server decide who keeps it. */}
      <Route
        path="/finance-receipts"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <FinanceReceiptsPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Dealer onboarding — enquiry, meeting, Sunny, LOI (27 Oct 2026); the server decides who may. */}
      <Route
        path="/dealer-onboarding"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <DealerOnboardingPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Attendance — an employee's own day and month, the owner's register; no permission, the page decides (13 Oct 2026). */}
      <Route
        path="/attendance"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <AttendancePage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* One's own Aadhaar and details — every employee (6 Oct 2026). */}
      <Route
        path="/my-verification"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <MyVerificationPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Each ASM's counters, sale by sale (owner, 13 Oct 2026). A territory ASM is
          held to their own by the server; everyone else picks an ASM or the team. */}
      <Route
        path="/sales-performance"
        element={
          <Protected permission="analytics:read">
            <AsmSalesPage />
          </Protected>
        }
      />

      {/* The field team, read from above: the month, the review, the incentive (3 Oct 2026). */}
      <Route
        path="/field-team"
        element={
          <Protected permission="field:review">
            <FieldTeamPage />
          </Protected>
        }
      />

      {/* The owner's lead sheets and the telecalling team (13 Oct 2026); the page decides who sees what. */}
      <Route
        path="/leads"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <LeadsPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The enquiry book — who asked and did not buy (12 Sep 2026); everyone's own, the owner's whole (13 Oct 2026). */}
      <Route
        path="/enquiries"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <EnquiriesPage />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      {/* The distributor's own view of what the maker sent it, and where each
          vehicle landed — read only (14 Sep 2026).

          Behind `billing:retail`, the distributor billing desk's own key, and
          not merely behind a sign-in (owner, 16 Sep 2026: "no asm nobody should
          have it"). Hiding the rail entry alone would have left the page one
          typed address away. */}
      <Route
        path="/from-autozone"
        element={
          <Protected permission="billing:retail">
            <DispatchFromAutozonePage />
          </Protected>
        }
      />

      {/* The distributor's billing desk — what the counters sold, and the bill
          against each (14 Sep 2026). */}
      <Route
        path="/billing"
        element={
          <Protected permission="billing:desk">
            <BillingDeskPage />
          </Protected>
        }
      />

      {/* Orders taken on calls — an ASM's own; the maker's desk gets the order desk (9 Sep 2026). */}
      <Route
        path="/orders"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <OrdersRoute />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <RequireAuth>
            <AppShell>
              <Suspense fallback={<RouteFallback />}>
                <OrdersRoute />
              </Suspense>
            </AppShell>
          </RequireAuth>
        }
      />

      <Route
        path="/dealers"
        element={
          <Protected permission="parties:read">
            <DealersPage />
          </Protected>
        }
      />
      <Route
        path="/dealers/:id"
        element={
          <Protected permission="parties:read">
            <DealerDetailPage />
          </Protected>
        }
      />

      <Route
        path="/sub-dealers"
        element={
          <Protected permission="parties:read">
            <NotInMakerBook>
              <SubDealersPage />
            </NotInMakerBook>
          </Protected>
        }
      />
      <Route
        path="/sub-dealers/:id"
        element={
          <Protected permission="parties:read">
            <SubDealerDetailPage />
          </Protected>
        }
      />

      <Route
        path="/outlets"
        element={
          <Protected permission="parties:read">
            <NotInMakerBook>
              <OutletsPage />
            </NotInMakerBook>
          </Protected>
        }
      />
      <Route
        path="/outlets/:id"
        element={
          <Protected permission="parties:read">
            <OutletDetailPage />
          </Protected>
        }
      />

      <Route
        path="/vehicles/:id"
        element={
          <Protected permission="vehicles:read">
            <VehicleProfilePage />
          </Protected>
        }
      />

      {/* The cross-ledger passport: staff only — a buyer's role never carries
          passport:read, and the API refuses scoped accounts besides. */}
      <Route
        path="/passport/:chassisNo"
        element={
          <Protected permission="passport:read">
            <PassportPage />
          </Protected>
        }
      />

      <Route
        path="/search"
        element={
          <Protected permission="vehicles:read">
            <SearchPage />
          </Protected>
        }
      />

      <Route
        path="/ageing"
        element={
          <Protected permission="vehicles:read">
            <AgeingPage />
          </Protected>
        }
      />

      <Route
        path="/analytics"
        element={
          <Protected permission="analytics:read">
            <AnalyticsPage />
          </Protected>
        }
      />

      <Route
        path="/sales"
        element={
          <Protected permission="analytics:read">
            <SalesPage />
          </Protected>
        }
      />

      <Route
        path="/returns"
        element={
          <Protected permission="vehicles:read">
            <ReturnsPage />
          </Protected>
        }
      />

      {/* The page itself sends anyone who neither approves nor holds vehicles:move home. */}
      <Route
        path="/transfers"
        element={
          <Protected permission="vehicles:read">
            <TransfersPage />
          </Protected>
        }
      />

      <Route
        path="/store"
        element={
          <Protected permission="vehicles:read">
            <AutozoneStorePage />
          </Protected>
        }
      />

      {/* Orders against stock, and what is left to build (owner, 25 Sep 2026).
          The maker's book only — the page sends any other book home. The desk
          reads it, and the factory's PDI hand by its own key (2 Oct 2026). */}
      <Route
        path="/production"
        element={
          <Protected permission={['requests:read', 'production:read']}>
            <ProductionPage />
          </Protected>
        }
      />

      {/* The check that comes before the store (owner, 16 Sep 2026). Reading the
          queue is `vehicles:read` — the office may watch it without clearing it;
          passing a check needs `vehicles:move`, which the page itself gates. */}
      <Route
        path="/pdi"
        element={
          <Protected permission="vehicles:read">
            <PdiPage />
          </Protected>
        }
      />
      {/* The Bright on Wheels ledger (28 Sep 2026) — the server scopes it: a
          counter its own sales, the office the whole book. */}
      <Route
        path="/ledger"
        element={
          <Protected permission="ledger:read">
            <LedgerPage />
          </Protected>
        }
      />
      {/* The factory register — Purchase data (28 Sep 2026). Read like the
          stores; its Upload section needs `chassis:upload`, which the page gates. */}
      <Route
        path="/purchase-data"
        element={
          <Protected permission="vehicles:read">
            <PurchaseDataPage />
          </Protected>
        }
      />

      <Route
        path="/activity"
        element={
          <Protected permission="activity:read">
            <ActivityPage />
          </Protected>
        }
      />

      <Route
        path="/my-sales"
        element={
          <Protected permission="vehicles:read">
            <MySalesPage />
          </Protected>
        }
      />

      {/* A counter's sold vehicles with their bill, insurance and papers to open and download (21 Oct 2026). */}
      <Route
        path="/sold-vehicles"
        element={
          <Protected permission="vehicles:read">
            <SoldPapersPage />
          </Protected>
        }
      />

      <Route
        path="/financiers"
        element={
          <Protected permission="parties:read">
            <FinanciersPage />
          </Protected>
        }
      />

      <Route
        path="/alerts"
        element={
          <Protected permission="alerts:read">
            <AlertsPage />
          </Protected>
        }
      />

      <Route
        path="/reports"
        element={
          <Protected permission="reports:run">
            <ReportsPage />
          </Protected>
        }
      />

      <Route
        path="/sync"
        element={
          <Protected permission="sync:read">
            <SyncPage />
          </Protected>
        }
      />

      {/* BUSY's exports into the TVS book, by hand (23 Sep 2026); every book's copy of BUSY and the check (21 Oct 2026). */}
      <Route
        path="/busy-data"
        element={
          <Protected permission="busy:upload">
            <BusyPage />
          </Protected>
        }
      />

      {/* The TVS quick sale: a vehicle marked sold with nothing else, for now (23 Sep 2026). */}
      <Route
        path="/tvs-sale"
        element={
          <Protected permission="vehicles:quick-sell">
            <QuickSalePage />
          </Protected>
        }
      />

      {/* The TVS dispatch: one vehicle off the TVS store to a mitr or an outlet
          (owner, 24 Sep 2026: "Design a dispatch form for tvs as well"). */}
      <Route
        path="/tvs-dispatch"
        element={
          <Protected permission="vehicles:move">
            <TvsDispatchPage />
          </Protected>
        }
      />

      {/* The TVS orders the dispatch hand sends against (owner, 19 Oct 2026). */}
      <Route
        path="/tvs-orders"
        element={
          <Protected permission="vehicles:move">
            <TvsOrdersPage />
          </Protected>
        }
      />

      {/* Where the dispatch sheet's photo links land (10 Sep 2026). */}
      <Route
        path="/photo/:id"
        element={
          <Protected permission="vehicles:read">
            <DispatchPhotoPage />
          </Protected>
        }
      />

      <Route
        path="/admin"
        element={<Navigate to="/admin/users" replace />}
      />
      <Route
        path="/admin/users"
        element={
          <Protected permission="users:read">
            <UsersPage />
          </Protected>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <Protected permission="roles:manage">
            <RolesPage />
          </Protected>
        }
      />
      <Route
        path="/admin/prices"
        element={
          <Protected permission="settings:manage">
            <PricesPage />
          </Protected>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <Protected permission="settings:manage">
            <SettingsPage />
          </Protected>
        }
      />

      <Route
        path="*"
        element={
          <RequireAuth>
            <AppShell>
              <NotFound />
            </AppShell>
          </RequireAuth>
        }
      />
    </Routes>
    </TerritoryBounds>
    </>
  );
}
